import { useMemo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { getLocales } from 'expo-localization';
import { ArrowLeft } from 'lucide-react-native';
import {
  roomsForCrew,
  WORKER_LANGS,
  type WorkerLang,
  colors,
  daysFor,
  roomTaskOptions,
  formatMinutes,
  pick,
  type Country,
  type Frequency,
  type SetupInput,
  type Snapshot,
  type Weekday,
} from '@hogar/shared';
import { api, ApiError } from '../lib/api';
import { useSession, useSetSnapshot } from '../lib/session';
import { Button, Card, ErrorText, Field, Label, Screen, Segmented, Txt, useT } from '../components/ui';
import { LanguagePicker } from '../components/LanguagePicker';
import { DaysPicker, Stepper, TimeStepper } from '../components/forms';
import { roomIcons } from '../components/icons';
import { Tick } from '../components/Tick';

const STEPS = 5;
const FREQ_KEY: Record<Frequency, 'freqDaily' | 'freqTwice' | 'freqWeekly'> = { daily: 'freqDaily', twice: 'freqTwice', weekly: 'freqWeekly' };

interface RoomPick {
  key: string;
  catalogId: string;
  name: { en: string; es: string };
}

/** First-run setup (spec): name the house, pick days, pick rooms, keep starter tasks, add the staff member. */
/** First-run setup offers the house's areas; the garden's come when an owner adds a gardener. */
const houseRooms = roomsForCrew('house');

export default function Setup() {
  const { t, lang } = useT();
  const { setLang } = useSession();
  const setSnapshot = useSetSnapshot();
  const locale = getLocales()[0];

  const [step, setStep] = useState(1);
  // Setup is timed (decision 53): only the number of seconds is sent.
  const [startedAt] = useState(() => Date.now());
  const [ownerName, setOwnerName] = useState('');
  const [houseName, setHouseName] = useState('');
  const [country, setCountry] = useState<Country>(locale?.regionCode === 'MX' ? 'MX' : 'US');
  const [workDays, setWorkDays] = useState<Weekday[]>([0, 1, 2, 3, 4]);
  const [workStart, setWorkStart] = useState('09:00');
  const [workEnd, setWorkEnd] = useState('15:00');
  const [counts, setCounts] = useState<Record<string, number>>(() => Object.fromEntries(houseRooms.map((r) => [r.id, r.common ? 1 : 0])));
  const [dropped, setDropped] = useState<Record<string, number[]>>({});
  const [staffName, setStaffName] = useState('');
  const [staffLang, setStaffLang] = useState<WorkerLang>('es');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // Each chosen room, numbered when a house has more than one ("Baño", "Baño 2").
  const picks: RoomPick[] = useMemo(
    () =>
      houseRooms.flatMap((r) =>
        Array.from({ length: counts[r.id] ?? 0 }, (_, i) => ({
          key: `${r.id}-${i}`,
          catalogId: r.id,
          name: i === 0 ? { en: r.name.en, es: r.name.es } : { en: `${r.name.en} ${i + 1}`, es: `${r.name.es} ${i + 1}` },
        })),
      ),
    [counts],
  );

  // Each room's own starter tasks begin ticked; ideas from rooms of the same kind begin unticked.
  const offFor = (p: RoomPick) =>
    dropped[p.key] ?? roomTaskOptions(p.catalogId).flatMap((o, i) => (o.on ? [] : [i]));
  const kept = (p: RoomPick) => roomTaskOptions(p.catalogId).map((_, i) => i).filter((i) => !offFor(p).includes(i));

  const weekly = picks.reduce(
    (acc, p) => {
      const options = roomTaskOptions(p.catalogId);
      for (const i of kept(p)) {
        acc.n += 1;
        acc.minutes += options[i].task.minutes * daysFor(options[i].task.frequency, workDays).length;
      }
      return acc;
    },
    { n: 0, minutes: 0 },
  );

  const canNext =
    (step === 1 && ownerName.trim() && houseName.trim()) ||
    (step === 2 && workDays.length > 0 && workEnd > workStart) ||
    (step === 3 && picks.length > 0) ||
    step === 4 ||
    step === 5;

  const finish = async () => {
    setBusy(true);
    setError('');
    const input: SetupInput = {
      ownerName: ownerName.trim(),
      lang,
      houseName: houseName.trim(),
      country,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || (country === 'MX' ? 'America/Mexico_City' : 'America/Chicago'),
      workDays,
      workStart,
      workEnd,
      rooms: picks.map((p) => ({ catalogId: p.catalogId, name: p.name, taskIndexes: kept(p) })),
      staffFirstName: staffName.trim() || undefined,
      staffLang,
      setupSeconds: Math.round((Date.now() - startedAt) / 1000),
    };
    try {
      setSnapshot(await api<Snapshot>('POST', '/setup', input));
    } catch (e) {
      setError(e instanceof ApiError && e.code === 'offline' ? t('errOffline') : t('errGeneric'));
      setBusy(false);
    }
  };

  return (
    <Screen>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 12 }}>
        {step > 1 ? (
          <Pressable onPress={() => setStep(step - 1)} hitSlop={12} style={{ width: 44, height: 44, justifyContent: 'center', marginLeft: -6 }} accessibilityLabel={t('back')}>
            <ArrowLeft size={24} color={colors.text} />
          </Pressable>
        ) : (
          <View style={{ height: 44 }} />
        )}
        <Txt size={14} color={colors.textMuted} style={{ flex: 1, textAlign: 'right' }}>
          {t('stepOf', { n: step, total: STEPS })}
        </Txt>
      </View>
      <View style={{ height: 6, backgroundColor: colors.primarySoft, borderRadius: 3, marginBottom: 18 }}>
        <View style={{ height: 6, width: `${(step / STEPS) * 100}%`, backgroundColor: colors.primary, borderRadius: 3 }} />
      </View>

      {step === 1 && (
        <>
          <Txt weight="semibold" size={24}>
            {t('setupHouse')}
          </Txt>
          <Label>{t('language')}</Label>
          <LanguagePicker value={lang} options={['es', 'en'] as const} onChange={setLang} />
          <Label>{t('yourName')}</Label>
          <Field value={ownerName} onChange={setOwnerName} autoComplete="name" />
          <Label>{t('houseName')}</Label>
          <Field value={houseName} onChange={setHouseName} placeholder={lang === 'es' ? 'Casa Pérez' : 'The Smith house'} />
          <Label>{t('country')}</Label>
          <Segmented value={country} options={[{ value: 'US', label: t('countryUS') }, { value: 'MX', label: t('countryMX') }]} onChange={setCountry} />
        </>
      )}

      {step === 2 && (
        <>
          <Txt weight="semibold" size={24}>
            {t('setupDays')}
          </Txt>
          <Label>{t('days')}</Label>
          <DaysPicker value={workDays} onChange={setWorkDays} lang={lang} />
          <Label>{t('startTime')}</Label>
          <TimeStepper value={workStart} onChange={setWorkStart} />
          <Label>{t('endTime')}</Label>
          <TimeStepper value={workEnd} onChange={setWorkEnd} />
        </>
      )}

      {step === 3 && (
        <>
          <Txt weight="semibold" size={24} style={{ marginBottom: 12 }}>
            {t('setupRooms')}
          </Txt>
          <Card>
            {houseRooms.map((r, i) => {
              const Icon = roomIcons[r.icon];
              const n = counts[r.id] ?? 0;
              return (
                <View key={r.id} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 64, borderBottomWidth: i === houseRooms.length - 1 ? 0 : 1, borderBottomColor: colors.divider }}>
                  <Icon size={22} color={colors.primary} strokeWidth={1.6} />
                  <Txt weight="medium" style={{ flex: 1 }}>
                    {pick(r.name, lang)}
                  </Txt>
                  {r.countable ? (
                    <Stepper value={n} min={0} max={6} onChange={(v) => setCounts({ ...counts, [r.id]: v })} />
                  ) : (
                    <Tick on={n > 0} onPress={() => setCounts({ ...counts, [r.id]: n > 0 ? 0 : 1 })} />
                  )}
                </View>
              );
            })}
          </Card>
        </>
      )}

      {step === 4 && (
        <>
          <Txt weight="semibold" size={24}>
            {t('setupTasks')}
          </Txt>
          <Txt color={colors.textMuted} style={{ marginBottom: 6 }}>
            {t('setupTasksSub')}
          </Txt>
          <Txt weight="medium" color={colors.primary} style={{ marginBottom: 12 }}>
            {t('perWeek', { n: weekly.n, time: formatMinutes(weekly.minutes) })}
          </Txt>
          {picks.map((p) => {
            const options = roomTaskOptions(p.catalogId);
            const off = offFor(p);
            return (
              <Card key={p.key}>
                <Txt weight="semibold" style={{ paddingTop: 12, paddingBottom: 4 }}>
                  {pick(p.name, lang)}
                </Txt>
                {options.map(({ task, on: suggested }, i) => {
                  const on = !off.includes(i);
                  const firstIdea = !suggested && (i === 0 || options[i - 1].on);
                  return (
                    <View key={i}>
                    {firstIdea && (
                      <Txt size={13} weight="medium" color={colors.textMuted} style={{ paddingTop: 12, paddingBottom: 4 }}>
                        {t('moreIdeasRoom')}
                      </Txt>
                    )}
                    <Pressable
                      onPress={() => setDropped({ ...dropped, [p.key]: on ? [...off, i] : off.filter((x) => x !== i) })}
                      style={{ flexDirection: 'row', alignItems: 'center', gap: 12, minHeight: 52, borderTopWidth: 1, borderTopColor: colors.divider }}
                    >
                      <Tick on={on} />
                      <View style={{ flex: 1 }}>
                        <Txt color={on ? colors.text : colors.textMuted}>{pick(task.name, lang)}</Txt>
                        <Txt size={13} color={colors.textMuted}>
                          {task.minutes} min · {t(FREQ_KEY[task.frequency])}
                        </Txt>
                      </View>
                    </Pressable>
                    </View>
                  );
                })}
              </Card>
            );
          })}
        </>
      )}

      {step === 5 && (
        <>
          <Txt weight="semibold" size={24}>
            {t('setupStaff')}
          </Txt>
          <Label>{t('firstName')}</Label>
          <Field value={staffName} onChange={setStaffName} placeholder="Rosa" />
          <Txt size={13} color={colors.textMuted} style={{ marginTop: 6 }}>
            {t('addLater')}
          </Txt>
          <Label>{t('workerLanguage')}</Label>
          <LanguagePicker list value={staffLang} options={WORKER_LANGS} onChange={setStaffLang} />
        </>
      )}

      <ErrorText>{error}</ErrorText>
      {step < STEPS ? (
        <Button label={t('next')} onPress={() => setStep(step + 1)} disabled={!canNext} style={{ marginTop: 22 }} />
      ) : (
        <Button label={t('createHouse')} onPress={finish} disabled={busy} style={{ marginTop: 22 }} />
      )}
    </Screen>
  );
}
